import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import PageShell from "@/components/proof/PageShell";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/data/products";
import { easing } from "@/lib/animations";

const QtyControl = ({ qty, onDec, onInc, testIdBase }) => (
    <div
        style={{
            display: "inline-flex",
            alignItems: "stretch",
            border: "1px solid rgba(255,255,255,0.35)",
        }}
    >
        <motion.button
            type="button"
            onClick={onDec}
            whileTap={{ scale: 0.85, backgroundColor: "rgba(255,255,255,0.08)" }}
            transition={{ duration: 0.18, ease: easing.primary }}
            data-testid={`${testIdBase}-dec`}
            style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 13,
                padding: "6px 12px",
                cursor: "pointer",
            }}
        >
            −
        </motion.button>
        <span
            data-testid={`${testIdBase}-qty`}
            style={{
                minWidth: 40,
                textAlign: "center",
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 13,
                letterSpacing: "0.18em",
                color: "#fff",
                padding: "6px 0",
                borderLeft: "1px solid rgba(255,255,255,0.2)",
                borderRight: "1px solid rgba(255,255,255,0.2)",
                overflow: "hidden",
                position: "relative",
            }}
        >
            <AnimatePresence mode="popLayout" initial={false}>
                <motion.span
                    key={qty}
                    initial={{ y: 16, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -16, opacity: 0 }}
                    transition={{ duration: 0.22, ease: easing.primary }}
                    style={{ display: "inline-block" }}
                >
                    {String(qty).padStart(2, "0")}
                </motion.span>
            </AnimatePresence>
        </span>
        <motion.button
            type="button"
            onClick={onInc}
            whileTap={{ scale: 0.85, backgroundColor: "rgba(255,255,255,0.08)" }}
            transition={{ duration: 0.18, ease: easing.primary }}
            data-testid={`${testIdBase}-inc`}
            style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 13,
                padding: "6px 12px",
                cursor: "pointer",
            }}
        >
            +
        </motion.button>
    </div>
);

export default function Cart() {
    const { items, count, subtotal, updateQty, removeItem, clearCart } = useCart();
    const navigate = useNavigate();

    const empty = items.length === 0;
    const SHIPPING_EUR = 0; // free shipping placeholder
    const total = subtotal + SHIPPING_EUR;

    return (
        <PageShell
            sectionNumber="04"
            eyebrow={empty ? "Empty signal" : `${String(count).padStart(2, "0")} items in hand`}
            title="Cart"
            crumb="Cart"
            data-testid="page-cart"
        >
            {empty ? (
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: easing.primary }}
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 28,
                        alignItems: "flex-start",
                        padding: "40px 0",
                    }}
                    data-testid="cart-empty"
                >
                    <p
                        style={{
                            margin: 0,
                            fontFamily: "'EB Garamond', serif",
                            fontStyle: "italic",
                            fontSize: 21,
                            color: "rgba(255,255,255,0.65)",
                            maxWidth: "44ch",
                        }}
                    >
                        Quiet so far. Choose what earns its place in your wardrobe — the field is open.
                    </p>
                    <Link
                        to="/shop"
                        data-testid="cart-empty-cta"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 12,
                            color: "#fff",
                            textDecoration: "none",
                            border: "1px solid rgba(255,255,255,0.45)",
                            padding: "16px 28px",
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 11,
                            letterSpacing: "0.28em",
                            textTransform: "uppercase",
                        }}
                    >
                        Enter Lookbook
                        <svg width="22" height="9" viewBox="0 0 22 9" fill="none" aria-hidden="true">
                            <line x1="0" y1="4.5" x2="20" y2="4.5" stroke="#fff" />
                            <path d="M16 1L20 4.5L16 8" stroke="#fff" fill="none" />
                        </svg>
                    </Link>
                </motion.div>
            ) : (
                <div className="proof-grid-main">
                    {/* LINES */}
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            borderTop: "1px solid rgba(255,255,255,0.14)",
                        }}
                        data-testid="cart-lines"
                    >
                        {items.map((it, i) => (
                            <motion.div
                                key={`${it.id}-${it.size}`}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 0.6,
                                    delay: 0.06 * i,
                                    ease: easing.primary,
                                }}
                                data-testid={`cart-line-${it.id}-${it.size}`}
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "120px 1fr auto",
                                    gap: 24,
                                    alignItems: "center",
                                    padding: "26px 0",
                                    borderBottom:
                                        "1px solid rgba(255,255,255,0.14)",
                                }}
                            >
                                <Link
                                    to={`/product/${it.slug}`}
                                    style={{
                                        background: "#1a1a1a",
                                        aspectRatio: "1 / 1",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        textDecoration: "none",
                                    }}
                                >
                                    <img
                                        src={it.img}
                                        alt={it.name}
                                        style={{
                                            width: "82%",
                                            height: "82%",
                                            objectFit: "contain",
                                            mixBlendMode: "screen",
                                            filter: "brightness(1.05)",
                                        }}
                                    />
                                </Link>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 8,
                                    }}
                                >
                                    <Link
                                        to={`/product/${it.slug}`}
                                        style={{
                                            color: "#fff",
                                            textDecoration: "none",
                                            fontFamily:
                                                "'Space Grotesk', sans-serif",
                                            fontSize: "clamp(16px, 1.2vw, 20px)",
                                            letterSpacing: "0.04em",
                                            textTransform: "uppercase",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {it.name}
                                    </Link>
                                    <span
                                        style={{
                                            fontFamily:
                                                "JetBrains Mono, monospace",
                                            fontSize: 11,
                                            letterSpacing: "0.24em",
                                            textTransform: "uppercase",
                                            color: "rgba(255,255,255,0.55)",
                                        }}
                                    >
                                        Size {it.size} · {it.id}
                                    </span>
                                    <div
                                        style={{
                                            marginTop: 10,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 14,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <QtyControl
                                            qty={it.qty}
                                            onDec={() =>
                                                updateQty(
                                                    it.id,
                                                    it.size,
                                                    it.qty - 1,
                                                )
                                            }
                                            onInc={() =>
                                                updateQty(
                                                    it.id,
                                                    it.size,
                                                    it.qty + 1,
                                                )
                                            }
                                            testIdBase={`cart-line-${it.id}-${it.size}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() =>
                                                removeItem(it.id, it.size)
                                            }
                                            data-testid={`cart-line-${it.id}-${it.size}-remove`}
                                            style={{
                                                background: "transparent",
                                                border: "none",
                                                color: "rgba(255,255,255,0.55)",
                                                fontFamily:
                                                    "JetBrains Mono, monospace",
                                                fontSize: 10,
                                                letterSpacing: "0.28em",
                                                textTransform: "uppercase",
                                                cursor: "pointer",
                                                padding: 0,
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                                <span
                                    style={{
                                        fontFamily:
                                            "JetBrains Mono, monospace",
                                        fontSize: 14,
                                        letterSpacing: "0.18em",
                                        color: "#fff",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {formatPrice(it.priceEUR * it.qty)}
                                </span>
                            </motion.div>
                        ))}

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                paddingTop: 22,
                            }}
                        >
                            <button
                                type="button"
                                onClick={clearCart}
                                data-testid="cart-clear"
                                style={{
                                    background: "transparent",
                                    border: "none",
                                    color: "rgba(255,255,255,0.5)",
                                    fontFamily: "JetBrains Mono, monospace",
                                    fontSize: 10,
                                    letterSpacing: "0.28em",
                                    textTransform: "uppercase",
                                    cursor: "pointer",
                                    padding: 0,
                                }}
                            >
                                Clear All
                            </button>
                            <Link
                                to="/shop"
                                style={{
                                    color: "rgba(255,255,255,0.65)",
                                    textDecoration: "none",
                                    fontFamily: "JetBrains Mono, monospace",
                                    fontSize: 11,
                                    letterSpacing: "0.28em",
                                    textTransform: "uppercase",
                                }}
                            >
                                ← Keep Browsing
                            </Link>
                        </div>
                    </div>

                    {/* SUMMARY */}
                    <aside
                        style={{
                            position: "sticky",
                            top: 120,
                            background: "rgba(255,255,255,0.04)",
                            border: "1px solid rgba(255,255,255,0.1)",
                            padding: "28px 26px",
                            display: "flex",
                            flexDirection: "column",
                            gap: 16,
                        }}
                        data-testid="cart-summary"
                    >
                        <h3
                            style={{
                                margin: 0,
                                fontFamily:
                                    "'Space Grotesk', Manrope, sans-serif",
                                fontWeight: 700,
                                fontSize: 22,
                                letterSpacing: "-0.015em",
                                textTransform: "uppercase",
                            }}
                        >
                            Summary
                        </h3>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 10,
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 12,
                                letterSpacing: "0.14em",
                                color: "rgba(255,255,255,0.75)",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                }}
                            >
                                <span>Subtotal</span>
                                <span data-testid="cart-subtotal">
                                    {formatPrice(subtotal)}
                                </span>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                }}
                            >
                                <span>Shipping</span>
                                <span>
                                    {SHIPPING_EUR === 0
                                        ? "Calculated at checkout"
                                        : formatPrice(SHIPPING_EUR)}
                                </span>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    color: "#fff",
                                    paddingTop: 12,
                                    borderTop:
                                        "1px solid rgba(255,255,255,0.18)",
                                    fontSize: 14,
                                    letterSpacing: "0.18em",
                                }}
                            >
                                <span>Total</span>
                                <span data-testid="cart-total">
                                    {formatPrice(total)}
                                </span>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => navigate("/checkout")}
                            data-testid="cart-checkout-cta"
                            style={{
                                width: "100%",
                                background: "#fff",
                                color: "#0c0c0c",
                                border: "none",
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 12,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                padding: "16px 0",
                                cursor: "pointer",
                                marginTop: 12,
                            }}
                        >
                            Proceed to Checkout
                        </button>

                        <p
                            style={{
                                margin: 0,
                                fontFamily: "'EB Garamond', serif",
                                fontStyle: "italic",
                                fontSize: 13,
                                color: "rgba(255,255,255,0.5)",
                                lineHeight: 1.5,
                            }}
                        >
                            Final payment is handled by Shopify Checkout.
                            Mocked for preview.
                        </p>
                    </aside>
                </div>
            )}
        </PageShell>
    );
}
